import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Heart, X, Loader2, ShieldCheck, Camera, Sparkles, MessageCircle, ArrowLeft, Send, ExternalLink, Users, MapPin } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

const INTEREST_OPTIONS = [
  "Music", "Nightlife", "Comedy", "Travel", "Food & Drink", "Fashion", "Fitness",
  "Movies", "Startups", "Reading", "Art", "Photography", "Gaming", "Sports", "Yoga",
];
const HOBBY_OPTIONS = ["Cooking", "Trekking", "Riding", "Dancing", "Singing", "Painting", "Podcasting", "Vlogging"];
const VIBES = [
  "House party & vibe all night",
  "Rooftop dinner & deep talks",
  "Concerts & festivals",
  "Sleep all night, brunch by noon",
  "Beach & road trips",
  "Gym + healthy food",
];
const YN = ["Yes", "No", "Socially"];
const RELATIONSHIP = ["Single", "Dating", "Open", "Prefer not to say"];
const WA_ADMIN = "https://wa.me/917250120666?text=Hi%20Eventom%20-%20I%27m%20a%20ClubVIP%20member%20and%20I%27d%20like%20to";

function toggleIn(arr, val, max = 999) {
  if (arr.includes(val)) return arr.filter((v) => v !== val);
  if (arr.length >= max) return arr;
  return [...arr, val];
}

async function uploadFile(file, kind) {
  // Auto-shrink huge camera photos so a 10MP selfie doesn't hit the 3MB wall.
  // For selfies + profile photos we're happy with a 1600px, ~1.5MB JPEG.
  const shouldCompress = kind?.startsWith("clubvip-");
  const compressed = shouldCompress ? await compressImage(file) : file;
  if (compressed.size > 3 * 1024 * 1024) {
    // Fallback: still too big after compression — try harder (1000px, ~900KB)
    const smaller = await compressImage(file, { maxDim: 1000, targetBytes: 900 * 1024 });
    if (smaller.size > 3 * 1024 * 1024) throw new Error("Photo too large, try a smaller one");
    return uploadCompressed(smaller, kind);
  }
  return uploadCompressed(compressed, kind);
}

async function uploadCompressed(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("eventom_token");
  const res = await fetch(`${API}/uploads?kind=${kind}`, { method: "POST", body: fd, credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
}

function ProfileEditor({ initial, onSaved, user }) {
  // Auto-populate from the user's main account profile the first time they land
  // on Match (initial === null). If a Match profile already exists, we trust
  // whatever they saved there and don't overwrite it.
  const derivedAge = React.useMemo(() => {
    if (initial?.age) return initial.age;
    if (!user?.date_of_birth) return "";
    try {
      const dob = new Date(user.date_of_birth);
      const now = new Date();
      let age = now.getFullYear() - dob.getFullYear();
      if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age -= 1;
      return age > 0 ? age : "";
    } catch { return ""; }
  }, [user, initial]);
  const [form, setForm] = useState(() => ({
    photos: initial?.photos || (user?.profile_picture ? [user.profile_picture] : []),
    live_selfie: initial?.live_selfie || "",
    age: initial?.age || derivedAge || "",
    gender: initial?.gender || user?.gender || "",
    city: initial?.city || user?.city || "",
    relationship_status: initial?.relationship_status || "Prefer not to say",
    // Vibes is now a multi-select array. Legacy single-string values still hydrate cleanly.
    vibes: initial?.vibes || (initial?.vibe ? [initial.vibe] : []),
    interests: initial?.interests || (user?.interests || []),
    hobbies: initial?.hobbies || [],
    career: initial?.career || "",
    smokes: initial?.smokes || "No",
    drinks: initial?.drinks || "No",
    bio: initial?.bio || "",
    lifestyle_answers: initial?.lifestyle_answers || { love_language: "", house_party_or_sleep: "", ideal_weekend: "" },
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  // Show a one-time "we prefilled from your profile" hint when we actually did.
  const prefilledCount = !initial ? [
    user?.profile_picture, user?.date_of_birth, user?.gender, user?.city, user?.interests?.length,
  ].filter(Boolean).length : 0;

  const pickPhoto = async (idx, ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setUploading(`photo-${idx}`);
    try {
      const url = await uploadFile(f, `clubvip-photo`);
      const photos = [...form.photos];
      photos[idx] = url;
      setForm({ ...form, photos: photos.filter(Boolean) });
    } catch (e) { toast.error(e.message); }
    finally { setUploading(""); ev.target.value = ""; }
  };
  const pickSelfie = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setUploading("selfie");
    try {
      const url = await uploadFile(f, `clubvip-selfie`);
      setForm({ ...form, live_selfie: url });
    } catch (e) { toast.error(e.message); }
    finally { setUploading(""); ev.target.value = ""; }
  };

  // Aadhaar (KYC) uploads — stored inside lifestyle_answers so the backend
  // Pydantic model (which accepts a free-form Dict[str, Any] there) persists
  // them without any schema change. Admin can read them off profile.lifestyle_answers.
  const pickAadhaar = (side) => async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setUploading(`aadhaar-${side}`);
    try {
      const url = await uploadFile(f, `clubvip-aadhaar-${side}`);
      setForm((s) => ({ ...s, lifestyle_answers: { ...(s.lifestyle_answers || {}), [`aadhaar_${side}`]: url } }));
    } catch (e) { toast.error(e.message); }
    finally { setUploading(""); ev.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.photos.length < 1) return toast.error("Add at least one public photo");
    if (!form.live_selfie) return toast.error("Take a live selfie for admin verification");
    if (form.interests.length < 3) return toast.error("Pick at least 3 interests");
    if (!form.age || form.age < 18) return toast.error("Age 18+ required");
    setSaving(true);
    try {
      // Backward compat: keep `vibe` populated with the first selection so
      // legacy consumers (email templates, share cards) still work while the
      // multi-value array becomes the source of truth.
      const payload = {
        ...form,
        age: Number(form.age) || null,
        vibe: form.vibes?.[0] || "",
      };
      const { data } = await api.post("/clubvip/profile", payload);
      toast.success(data.verification_status === "approved" ? "Profile saved — you're verified!" : "Profile submitted — admin will verify your live selfie shortly");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="glass rounded-3xl p-6 space-y-5" data-testid="clubvip-profile-form">
      {/* ONBOARDING — guided 3-step banner, only shown on the very first visit.
          Once the user has any saved profile (initial !== null), we hide this. */}
      {!initial && (
        <div className="rounded-3xl border border-lime/30 bg-gradient-to-br from-lime/10 via-skyx/5 to-magenta/10 p-5" data-testid="clubvip-onboarding">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-1">How Eventom Match works</div>
          <div className="font-fancy text-2xl text-white leading-tight">Three steps. <span className="grad-text-hot">One vibe check.</span></div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {[
              { n: "1", t: "Build your card", d: "3 public photos + 1 live selfie for admin verification." },
              { n: "2", t: "Pick your event", d: "Book a ticket or RSVP — Match scopes the deck to your night." },
              { n: "3", t: "Mutual likes only", d: "Only when you both hit like do names & full profiles unlock." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="w-7 h-7 rounded-full bg-lime text-ink font-mono font-bold text-xs flex items-center justify-center mb-2">{s.n}</div>
                <div className="font-heading font-bold text-sm text-white leading-tight">{s.t}</div>
                <div className="text-[11px] text-white/70 mt-1 leading-snug">{s.d}</div>
              </div>
            ))}
          </div>
          {prefilledCount > 0 && (
            <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-skyx" data-testid="clubvip-prefill-hint">
              ✓ Prefilled {prefilledCount} field{prefilledCount === 1 ? "" : "s"} from your account — tweak anything below.
            </div>
          )}
        </div>
      )}
      <div>
        <div className="font-display text-3xl">Your ClubVIP profile</div>
        <div className="text-xs text-muted mt-1">Only your first name, blurred hero photo and top 3 interests are visible until it's a mutual love match. Everything else stays private.</div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">3 public photos <span className="text-muted normal-case tracking-normal">· Recommended 1080×1080 · Square · auto-shrunk in browser</span></div>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <label key={i} className="aspect-square rounded-2xl border border-white/15 bg-white/5 flex items-center justify-center overflow-hidden cursor-pointer relative" data-testid={`clubvip-photo-${i}`}>
              {form.photos[i] ? (
                <img src={form.photos[i]} alt="" className="w-full h-full object-cover" />
              ) : uploading === `photo-${i}` ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted" />
              ) : (
                <Camera className="w-5 h-5 text-muted" />
              )}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(i, e)} />
            </label>
          ))}
        </div>
      </div>

      {/* Live selfie — mockup-style bigger tappable verification step */}
      <div className="rounded-3xl border border-magenta/40 bg-gradient-to-br from-magenta/15 via-purple/10 to-lime/10 p-5" data-testid="clubvip-selfie-block">
        <div className="flex items-start justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-magenta"><ShieldCheck className="w-3.5 h-3.5" /> Live selfie verification</div>
          {form.live_selfie && <span className="chip !text-lime !border-lime/40 !bg-lime/10 !text-[10px]">Ready for review</span>}
        </div>
        <div className="mt-3 grid sm:grid-cols-[160px,1fr] gap-4 items-center">
          <label className="relative cursor-pointer group" data-testid="clubvip-selfie-upload">
            <div className="w-36 h-36 sm:w-40 sm:h-40 rounded-3xl overflow-hidden border-2 border-dashed border-magenta/50 group-hover:border-magenta bg-black/40 flex items-center justify-center transition-colors mx-auto">
              {form.live_selfie ? (
                <img src={form.live_selfie} alt="" className="w-full h-full object-cover" />
              ) : uploading === "selfie" ? (
                <Loader2 className="w-8 h-8 animate-spin text-magenta" />
              ) : (
                <div className="text-center px-3">
                  <Camera className="w-8 h-8 text-magenta mx-auto" />
                  <div className="text-[10px] font-heading font-bold uppercase tracking-widest text-white/85 mt-2">Tap to take selfie</div>
                </div>
              )}
            </div>
            <input type="file" accept="image/*" capture="user" className="hidden" onChange={pickSelfie} />
          </label>
          <div>
            <div className="font-heading font-bold text-white text-base leading-tight">One quick selfie. That's it.</div>
            <ul className="mt-2 space-y-1 text-[12px] text-white/75">
              <li className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Only admin sees it — never shown on your card</li>
              <li className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Verified badge in ~1 hour on match days</li>
              <li className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Keeps the deck real — no bots, no catfish</li>
            </ul>
          </div>
        </div>
      </div>

      {/* KYC — Aadhaar (front + back) + Instagram handle. Stored inside
          lifestyle_answers so no backend schema change is required. */}
      <div className="rounded-3xl border border-skyx/40 bg-gradient-to-br from-skyx/10 via-purple/5 to-lime/10 p-5" data-testid="clubvip-kyc-block">
        <div className="text-[10px] font-mono uppercase tracking-widest text-skyx inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> KYC · Invite-only requirement
        </div>
        <div className="font-heading font-bold text-white text-base mt-1.5">Aadhaar + Instagram — one time.</div>
        <div className="text-[11px] text-white/70 mt-0.5">Reviewed by admin. Only used to verify you're a real human before you enter the circle.</div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["front", "back"].map((side) => {
            const url = form.lifestyle_answers?.[`aadhaar_${side}`];
            return (
              <label key={side} className="cursor-pointer group" data-testid={`clubvip-aadhaar-${side}-upload`}>
                <div className="rounded-2xl border-2 border-dashed border-skyx/40 group-hover:border-skyx bg-black/40 aspect-[16/10] flex items-center justify-center overflow-hidden transition-colors">
                  {url ? (
                    <img src={url} alt={`aadhaar ${side}`} className="w-full h-full object-cover" />
                  ) : uploading === `aadhaar-${side}` ? (
                    <Loader2 className="w-6 h-6 animate-spin text-skyx" />
                  ) : (
                    <div className="text-center px-3">
                      <ShieldCheck className="w-6 h-6 text-skyx mx-auto" />
                      <div className="text-[10px] font-heading font-bold uppercase tracking-widest text-white/85 mt-2">Aadhaar {side} · tap to upload</div>
                    </div>
                  )}
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={pickAadhaar(side)} />
              </label>
            );
          })}
        </div>
        <div className="mt-4">
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Instagram handle</label>
          <div className="mt-1 flex items-center gap-2 rounded-2xl bg-black/40 border border-skyx/30 pl-3 pr-1 py-1 focus-within:border-skyx transition-colors">
            <span className="text-skyx font-mono text-sm">@</span>
            <input
              value={form.lifestyle_answers?.instagram_handle || ""}
              onChange={(e) => setForm((s) => ({ ...s, lifestyle_answers: { ...(s.lifestyle_answers || {}), instagram_handle: e.target.value.replace(/^@+/, "") } }))}
              placeholder="yourhandle"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/40 py-2"
              data-testid="clubvip-ig-handle"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Age (18+)</label><input required type="number" min={18} max={120} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="input-dark !mt-1" data-testid="clubvip-age" /></div>
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">City</label><input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark !mt-1" data-testid="clubvip-city" /></div>
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Gender</label>
          <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="input-dark !mt-1" data-testid="clubvip-gender">
            <option value="" className="bg-bg">Select…</option>
            {["Female", "Male", "Non-binary", "Prefer not to say"].map((g) => <option key={g} value={g.toLowerCase()} className="bg-bg">{g}</option>)}
          </select>
        </div>
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Relationship</label>
          <select value={form.relationship_status} onChange={(e) => setForm({ ...form, relationship_status: e.target.value })} className="input-dark !mt-1" data-testid="clubvip-rel">
            {RELATIONSHIP.map((r) => <option key={r} value={r} className="bg-bg">{r}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-magenta mb-2">Interests (pick 3–6; top 3 are shown publicly)</div>
        <div className="flex flex-wrap gap-2" data-testid="clubvip-interests">
          {INTEREST_OPTIONS.map((k) => (
            <button type="button" key={k} onClick={() => setForm({ ...form, interests: toggleIn(form.interests, k, 6) })}
              className={`chip !text-[10px] ${form.interests.includes(k) ? "!bg-magenta !border-magenta !text-white" : ""}`}>{k}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">Hobbies</div>
        <div className="flex flex-wrap gap-2" data-testid="clubvip-hobbies">
          {HOBBY_OPTIONS.map((k) => (
            <button type="button" key={k} onClick={() => setForm({ ...form, hobbies: toggleIn(form.hobbies, k) })}
              className={`chip !text-[10px] ${form.hobbies.includes(k) ? "!bg-lime !border-lime !text-ink" : ""}`}>{k}</button>
          ))}
        </div>
      </div>

      <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Career / What you do</label>
        <input value={form.career} onChange={(e) => setForm({ ...form, career: e.target.value })} className="input-dark !mt-1" data-testid="clubvip-career" placeholder="e.g. product designer @ startup" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Smoke?</label>
          <div className="flex gap-1.5 mt-1" data-testid="clubvip-smoke">{YN.map((v) => <button type="button" key={v} onClick={() => setForm({ ...form, smokes: v })} className={`chip !text-[10px] ${form.smokes === v ? "!bg-white/20 !text-white" : ""}`}>{v}</button>)}</div>
        </div>
        <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Drink?</label>
          <div className="flex gap-1.5 mt-1" data-testid="clubvip-drink">{YN.map((v) => <button type="button" key={v} onClick={() => setForm({ ...form, drinks: v })} className={`chip !text-[10px] ${form.drinks === v ? "!bg-white/20 !text-white" : ""}`}>{v}</button>)}</div>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-skyx mb-2">Your vibes <span className="text-muted normal-case tracking-normal">· pick as many as fit you</span></div>
        <div className="grid gap-2 sm:grid-cols-2" data-testid="clubvip-vibe">
          {VIBES.map((v) => {
            const active = form.vibes.includes(v);
            return (
              <button type="button" key={v}
                onClick={() => setForm({ ...form, vibes: toggleIn(form.vibes, v) })}
                data-testid={`clubvip-vibe-${v.split(" ").slice(0, 2).join("-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}
                className={`text-left rounded-2xl border p-3 text-sm transition-all ${active ? "border-lime bg-lime/15 text-white shadow-[0_0_0_2px_rgba(170,238,0,0.2)]" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                {v}
                {active && <span className="ml-2 text-lime text-[10px] font-mono uppercase tracking-widest">✓ Picked</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Short bio (unlocked after a match)</label>
        <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="input-dark !mt-1 resize-none" data-testid="clubvip-bio" placeholder="A line or two about you" />
      </div>

      <button disabled={saving} className="btn-primary w-full justify-center" data-testid="clubvip-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Save profile
      </button>
      <div className="text-[10px] text-muted text-center">All details are private. Only your first name, blurred hero photo and top 3 interests are shown until you and someone else both hit Love Match.</div>
    </form>
  );
}

function Deck({ city, eventId, eventTitle, onClearEvent }) {
  const [deck, setDeck] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (eventId) params.set("event_id", eventId);
    else if (city) params.set("city", city);
    api.get(`/clubvip/discover${params.toString() ? `?${params.toString()}` : ""}`).then(({ data }) => { setDeck(data.deck || []); setIdx(0); }).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Deck unavailable");
    }).finally(() => setLoading(false));
  }, [city, eventId]);

  const current = deck[idx];
  const swipe = async (action) => {
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await api.post("/clubvip/swipe", { to_user_id: current.id, action });
      if (data.match) toast.success("💕 It's a Love Match! Check Matches tab to chat.");
      setIdx((i) => i + 1);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="glass p-6 font-mono text-sm text-muted">Loading deck…</div>;

  const EventBanner = eventId && eventTitle ? (
    <div className="glass rounded-2xl p-3 mb-4 flex items-center justify-between gap-2" data-testid="deck-event-banner">
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Event mode</div>
        <div className="font-heading font-bold text-sm truncate">Attending {eventTitle}</div>
      </div>
      <button onClick={onClearEvent} data-testid="deck-clear-event" className="text-[10px] font-mono uppercase tracking-widest text-muted underline hover:text-white">Show all cities →</button>
    </div>
  ) : null;

  if (!current) return (
    <>
      {EventBanner}
      <div className="glass p-8 text-center" data-testid="deck-empty">
        <Sparkles className="w-8 h-8 text-lime mx-auto mb-3" />
        <div className="font-heading font-bold text-lg">{eventId ? "No matches at this event yet." : "You're all caught up."}</div>
        <div className="text-sm text-muted mt-1">{eventId ? "Be the first to show up. Invite a friend to make it worth watching." : "New verified profiles unlock here every day. Meanwhile, DM admin on WhatsApp to host or join a private party."}</div>
        <a href={WA_ADMIN} target="_blank" rel="noreferrer" className="btn-purple mt-4 inline-flex" data-testid="deck-wa"><MessageCircle className="w-4 h-4" /> DM admin on WhatsApp</a>
      </div>
    </>
  );

  return (
    <div className="max-w-md mx-auto" data-testid="clubvip-deck">
      {EventBanner}
      <div className="glass rounded-3xl overflow-hidden">
        {current.blurred_photo ? (
          <div className="aspect-[4/5] overflow-hidden">
            <img src={current.blurred_photo} alt={current.first_name} className="w-full h-full object-cover" style={{ filter: "blur(14px) saturate(1.05)" }} />
          </div>
        ) : (
          <div className="aspect-[4/5] bg-gradient-to-br from-magenta/40 via-purple/40 to-lime/20 flex items-center justify-center"><Crown className="w-14 h-14 text-white/60" /></div>
        )}
        <div className="p-5">
          <div className="font-display text-3xl">{current.first_name}<span className="text-muted text-xl">, {current.age || "—"}</span></div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-0.5">{current.city || "Somewhere in India"}</div>
          {current.vibe && <div className="mt-3 text-sm text-white/80 italic">"{current.vibe}"</div>}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(current.top_interests || []).map((t) => <span key={t} className="chip !text-[10px] !text-magenta !border-magenta/40">{t}</span>)}
          </div>
          <div className="mt-5 flex items-center justify-center gap-5">
            <button disabled={busy} onClick={() => swipe("pass")} data-testid="deck-pass" className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20"><X className="w-6 h-6" /></button>
            <button disabled={busy} onClick={() => swipe("like")} data-testid="deck-like" className="w-16 h-16 rounded-full bg-magenta text-white shadow-[0_10px_40px_rgba(255,46,154,0.5)] flex items-center justify-center hover:scale-105 transition"><Heart className="w-7 h-7" fill="currentColor" /></button>
          </div>
          <div className="text-center text-[10px] font-mono uppercase tracking-widest text-muted mt-3">
            {idx + 1} of {deck.length} in the deck
          </div>
        </div>
      </div>
    </div>
  );
}

function Matches({ onOpen }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/clubvip/matches").then(({ data }) => setMatches(data.matches || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="glass p-6 font-mono text-sm text-muted">Loading matches…</div>;
  if (matches.length === 0) return (
    <div className="glass p-8 text-center" data-testid="matches-empty">
      <Heart className="w-8 h-8 text-magenta mx-auto mb-3" />
      <div className="font-heading font-bold text-lg">No matches yet.</div>
      <div className="text-sm text-muted mt-1">When someone you liked likes you back, you'll unlock the full profile + chat here.</div>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="clubvip-matches">
      {matches.map((m) => (
        <button key={m.match_id} onClick={() => onOpen(m)} data-testid={`match-${m.match_id}`} className="glass rounded-3xl overflow-hidden card-lift text-left">
          {m.photos?.[0] ? <div className="aspect-[4/3] overflow-hidden"><img src={m.photos[0]} alt={m.name} className="w-full h-full object-cover" /></div> : <div className="aspect-[4/3] bg-purple/30" />}
          <div className="p-4">
            <div className="font-display text-2xl">{m.name}<span className="text-muted text-base">, {m.age || "—"}</span></div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{m.city}</div>
            {m.vibe && <div className="text-xs text-white/70 mt-1 italic line-clamp-1">"{m.vibe}"</div>}
            <div className="mt-2 flex flex-wrap gap-1">{(m.top_interests || []).map((t) => <span key={t} className="chip !text-[9px]">{t}</span>)}</div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-lime inline-flex items-center gap-1">Open chat <MessageCircle className="w-3 h-3" /></div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatView({ match, onBack, meId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const load = () => api.get(`/clubvip/chat/${match.match_id}`).then(({ data }) => setMsgs(data.messages || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-line */ }, [match.match_id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/clubvip/chat/send", { match_id: match.match_id, text }); setText(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-3xl overflow-hidden flex flex-col h-[70vh]" data-testid="clubvip-chat">
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <button onClick={onBack} className="icon-circle !w-8 !h-8 bg-white/5" data-testid="chat-back"><ArrowLeft className="w-4 h-4" /></button>
        {match.photos?.[0] && <img src={match.photos[0]} alt={match.name} className="w-10 h-10 rounded-full object-cover" />}
        <div className="min-w-0">
          <div className="font-heading font-bold">{match.name}</div>
          <div className="text-[10px] font-mono text-muted">{match.city}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.length === 0 && <div className="text-center text-xs font-mono text-muted mt-6">Say hi 👋</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.from_user_id === meId ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${m.from_user_id === meId ? "bg-magenta text-white" : "bg-white/10 text-white"}`}>{m.text}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input required value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="input-dark flex-1" data-testid="chat-text" />
        <button disabled={busy} className="btn-lime !py-2 !px-4" data-testid="chat-send"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

export default function ClubVIP() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const eventId = params.get("event") || "";
  const [eventTitle, setEventTitle] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("deck");
  const [openMatch, setOpenMatch] = useState(null);

  useEffect(() => {
    if (!eventId) { setEventTitle(""); return; }
    api.get(`/events/${eventId}`).then(({ data }) => setEventTitle(data.title || "")).catch(() => setEventTitle(""));
  }, [eventId]);

  const clearEvent = () => { setParams({}); };

  const load = () => {
    api.get("/clubvip/profile/me").then(({ data }) => setProfile(data.profile))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { document.title = "Eventom Match — Meet people going where you're going"; if (user) load(); else setLoading(false); }, [user]);

  if (loading) return <Layout><div className="max-w-3xl mx-auto px-4 py-12 font-mono text-sm text-muted">Loading…</div></Layout>;

  if (!user) return (
    <Layout><section className="relative overflow-hidden poster-canvas border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="font-fancy uppercase text-white leading-[0.88]" style={{ fontSize: "clamp(42px, 6.8vw, 80px)" }}>
            The event ends.<br/>
            <span className="marker-lime">The vibe</span><br/>
            doesn't.
          </h1>
          <div className="mt-6 h-[3px] w-40 bg-lime/50 rounded-full" />
          <p className="mt-6 text-white/75 text-base max-w-md leading-relaxed">
            Where the real night begins — <span className="text-lime font-bold">exclusive after-parties</span>, VIP access, and moments the crowd never sees.
          </p>
          <Link to="/login" className="btn-poster mt-8" data-testid="clubvip-login-cta">Join the VIP →</Link>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-white/60">Exclusive access · Limited invites</div>
          <div className="mt-8 signature-script text-2xl md:text-3xl">More than events.</div>
        </div>
        <div className="rounded-3xl overflow-hidden aspect-[4/5] relative">
          <img src="/assets/festival-girls.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute top-6 right-6 text-lime font-fancy leading-none tracking-tight text-4xl md:text-5xl" style={{ textShadow: "0 0 22px rgba(216,255,0,0.65)" }}>
            EVENTOM<br/><span className="text-6xl md:text-7xl">VIP</span>
          </div>
        </div>
      </div>
    </section></Layout>
  );

  const verified = profile?.selfie_verified;

  return (
    <Layout>
      {/* ═══════ HERO — Poster-grade Inner Circle ═══════ */}
      <section className="relative overflow-hidden poster-canvas border-b border-white/5" data-testid="clubvip-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
          {/* LEFT — Poster copy */}
          <div className="relative z-10">
            <h1 className="font-fancy uppercase text-white leading-[0.88]" style={{ fontSize: "clamp(44px, 7vw, 84px)" }}>
              The event ends.<br/>
              <span className="marker-lime">The vibe</span><br/>
              doesn't.
            </h1>
            <div className="mt-6 max-w-md h-[3px] w-40 bg-lime/50 rounded-full" />
            <p className="mt-6 text-white/75 text-base max-w-md leading-relaxed">
              Where the real night begins — <span className="text-lime font-bold">exclusive after-parties</span>, VIP access, and moments the crowd never sees.
            </p>

            <div className="mt-8 flex flex-wrap gap-6" data-testid="clubvip-poster-trio">
              {[
                { label: "After-Parties" },
                { label: "VIP Access" },
                { label: "Premium Moments" },
              ].map((x) => (
                <div key={x.label} className="text-white/90">
                  <div className="w-10 h-10 border border-lime/60 rounded-lg flex items-center justify-center mb-2">
                    <span className="w-2 h-2 bg-lime rounded-full" />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest">{x.label}</div>
                </div>
              ))}
            </div>

            {!profile && (
              <a href="#apply" className="btn-poster mt-10" data-testid="clubvip-apply-cta">Join the VIP →</a>
            )}
            <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-white/60">Exclusive access · Limited invites</div>
            <div className="mt-10 signature-script text-2xl md:text-3xl">More than events.</div>
          </div>

          {/* RIGHT — Real portrait, poster style */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/5] relative">
              <img src="/assets/festival-girls.webp" alt="Eventom VIP crowd" className="w-full h-full object-cover" data-testid="clubvip-hero-portrait" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute top-6 right-6 text-lime font-fancy text-4xl md:text-5xl leading-none tracking-tight" style={{ textShadow: "0 0 22px rgba(216,255,0,0.65)" }}>
                EVENTOM<br/><span className="text-6xl md:text-7xl">VIP</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ WHAT'S INSIDE — icon grid ═══════ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="clubvip-whats-inside" data-reveal="1">
        <div className="mb-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-1">What's inside</div>
          <h2 className="font-display text-3xl md:text-5xl text-white">Everything you signed up for.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { Icon: Sparkles,      title: "After Parties",     sub: "Private guest lists" },
            { Icon: Users,         title: "House Parties",     sub: "Curated intimate crowds" },
            { Icon: MapPin,        title: "Private Trips",     sub: "Weekend getaways" },
            { Icon: Crown,         title: "Luxury Experiences",sub: "Members-only tastings" },
            { Icon: Heart,         title: "Matchmaking",       sub: "Event-based, verified" },
            { Icon: ShieldCheck,   title: "Contests",          sub: "Members-only prizes" },
            { Icon: MessageCircle, title: "Community Feed",    sub: "Real people, real posts" },
            { Icon: Camera,        title: "AI Memories",       sub: "Photos + vibe cards" },
          ].map(({ Icon, title, sub }) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-magenta/50">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-magenta/15 text-magenta border border-magenta/30 mb-3"><Icon className="w-5 h-5" /></span>
              <div className="font-heading font-bold text-white text-sm">{title}</div>
              <div className="text-[11px] text-muted mt-1">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 relative" data-testid="clubvip-page" id="apply">
        {/* Themed-blue Rizz backdrop for the Match Room area (PDF ref) */}
        {profile && verified && !openMatch && (
          <div aria-hidden className="absolute inset-x-0 top-0 h-[520px] themed-blue sticker-wall rounded-b-[48px] -z-0 opacity-[0.92]" />
        )}
        <div className="relative z-10">

        {profile && profile.selfie_verified && null}

        {!profile ? (
          <ProfileEditor initial={null} onSaved={load} user={user} />
        ) : !verified ? (
          <>
            <div className="glass p-5 mb-4 border border-orangex/40" data-testid="clubvip-pending-banner">
              <div className="font-heading font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orangex" /> Waiting for admin verification</div>
              <div className="text-xs text-muted mt-1">Your live selfie is being reviewed. Once approved, your profile enters the deck and you'll see other verified members.</div>
            </div>
            <ProfileEditor initial={profile} onSaved={load} user={user} />
          </>
        ) : openMatch ? (
          <ChatView match={openMatch} meId={user.id} onBack={() => setOpenMatch(null)} />
        ) : (
          <div className="grid lg:grid-cols-12 gap-6" data-testid="match-layout">
            {/* LEFT RAIL — event list + Match Safely */}
            <aside className="lg:col-span-3 space-y-4" data-testid="match-left-rail">
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">Event-based matching</div>
                <div className="font-heading font-bold text-white text-lg mt-1 leading-tight">Meet people going<br/>where you're going.</div>
                <div className="inline-flex gap-1 p-1 rounded-full bg-white/5 mt-4 text-[10px] font-mono uppercase tracking-widest">
                  <button onClick={() => setTab("deck")} className={`px-3 py-1 rounded-full ${tab === "deck" ? "bg-magenta text-white" : "text-white/60"}`} data-testid="match-tab-discover">Discover</button>
                  <button onClick={() => setTab("matches")} className={`px-3 py-1 rounded-full ${tab === "matches" ? "bg-magenta text-white" : "text-white/60"}`} data-testid="match-tab-matches">Matches</button>
                  <button onClick={() => setTab("profile")} className={`px-3 py-1 rounded-full ${tab === "profile" ? "bg-magenta text-white" : "text-white/60"}`} data-testid="match-tab-profile">Me</button>
                </div>
                <MyEventShortlist eventId={eventId} eventTitle={eventTitle} onClearEvent={clearEvent} />
              </div>

              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-magenta/15 to-purple/10 p-5" data-testid="match-safely">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-magenta"><ShieldCheck className="w-3.5 h-3.5" /> Match Safely</div>
                <ul className="mt-3 space-y-1.5 text-xs text-white/80">
                  <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Verified profiles</li>
                  <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Event-based matching only</li>
                  <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Your privacy is in control</li>
                  <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime" /> Block or report anytime</li>
                </ul>
              </div>

              <div className="rounded-3xl border border-white/10 bg-[#0F0F16] p-5">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-lime"><Crown className="w-3.5 h-3.5" /> Eventom Match</div>
                <div className="font-fancy text-2xl text-white mt-2 leading-none">Same events.<br/><span className="grad-text-hot">Better people.</span></div>
                <ul className="mt-3 space-y-1.5 text-[11px] text-white/70">
                  <li>Verified Profiles</li>
                  <li>Event-based Matching</li>
                  <li>Real People, Real Interests</li>
                  <li>Your Privacy, Our Priority</li>
                </ul>
              </div>
            </aside>

            {/* CENTER — Deck / Matches / Profile */}
            <div className="lg:col-span-6" data-testid="match-center">
              {eventId && eventTitle && (
                <div className="rounded-2xl border border-lime/40 bg-lime/10 p-3 mb-4 flex items-center justify-between gap-2" data-testid="match-event-banner">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Attending</div>
                    <div className="font-heading font-bold text-sm truncate text-white">{eventTitle}</div>
                  </div>
                  <button onClick={clearEvent} className="text-[10px] font-mono uppercase tracking-widest text-lime underline">Show all cities →</button>
                </div>
              )}
              {tab === "deck" && <Deck city={profile.city} eventId={eventId} eventTitle={eventTitle} onClearEvent={clearEvent} />}
              {tab === "matches" && <Matches onOpen={(m) => { setOpenMatch(m); }} />}
              {tab === "profile" && <ProfileEditor initial={profile} onSaved={load} user={user} />}
            </div>

            {/* RIGHT RAIL — Your Matches preview + Same Vibes */}
            <aside className="lg:col-span-3 space-y-4" data-testid="match-right-rail">
              <MatchesRail onOpen={(m) => setOpenMatch(m)} />
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-skyx/15 via-magenta/10 to-orangex/15 p-5 relative overflow-hidden" data-testid="match-vibes-card">
                <div className="font-script text-2xl text-white leading-tight -rotate-2">Same Vibes<br/><span className="text-lime">New Friends</span></div>
                <div className="text-xs text-white/75 mt-3 leading-relaxed">More than just events. It's a community.</div>
                <img src="/assets/girl-street.webp" alt="" className="absolute -right-6 -bottom-4 w-28 h-28 rounded-2xl object-cover opacity-40" />
              </div>
            </aside>
          </div>
        )}
        </div>
      </section>

      {/* ═══════ COMMUNITY · CONTESTS · OPPORTUNITIES — live data ═══════ */}
      {profile && verified && (
        <ClubVipContentRails />
      )}
    </Layout>
  );
}

// ─── Live-data rails (Feed / Contests / Opportunities) ───────────────────────
function ClubVipContentRails() {
  const [feed, setFeed] = useState([]);
  const [contests, setContests] = useState([]);
  const [ops, setOps] = useState([]);
  const [enteredIds, setEnteredIds] = useState([]);
  useEffect(() => {
    api.get("/clubvip/feed").then(({ data }) => setFeed(data || [])).catch(() => {});
    api.get("/clubvip/contests").then(({ data }) => setContests(data || [])).catch(() => {});
    api.get("/clubvip/opportunities").then(({ data }) => setOps(data || [])).catch(() => {});
    api.get("/clubvip/contest-entries/me").then(({ data }) => setEnteredIds(data?.contest_ids || [])).catch(() => {});
  }, []);

  const enterContest = async (c) => {
    try {
      const { data } = await api.post("/clubvip/contest-entries", { contest_id: c.id });
      setEnteredIds((prev) => prev.includes(c.id) ? prev : [...prev, c.id]);
      if (data.already_entered) toast("You're already entered ✓");
      else toast.success("You're in — good luck!");
      if (data.entry_link) window.open(data.entry_link, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't enter");
    }
  };

  return (
    <>
      {/* Community Feed */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="clubvip-community-feed" data-reveal="1">
        <div className="mb-5 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Community Feed</div>
            <h2 className="font-display text-3xl md:text-4xl text-white">Insider posts.</h2>
          </div>
        </div>
        {feed.length === 0 ? (
          <div className="glass p-8 rounded-3xl text-center text-sm text-white/70">
            First drop coming soon. Admin posts and member spotlights land here.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {feed.map((p) => (
              <article key={p.id} className="rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden card-mag" data-testid={`feed-post-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt="" className="w-full aspect-[16/9] object-cover" />}
                <div className="p-5">
                  {p.pinned && <span className="chip !text-[9px] !text-magenta !border-magenta/50 mb-2">Pinned</span>}
                  <h3 className="font-heading font-bold text-white text-lg leading-tight">{p.title}</h3>
                  <p className="text-[13px] text-white/75 mt-2 line-clamp-4">{p.body}</p>
                  {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="inline-block mt-3 text-[11px] font-mono uppercase tracking-widest text-lime hover:underline">Open →</a>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Contests — themed PURPLE "HIGH-FOUNDING" gradient (PDF ref) */}
      <section className="relative themed-purple sticker-wall py-14 overflow-hidden" data-testid="clubvip-contests" data-reveal="1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/80">High · Founding · Members</div>
              <h2 className="font-fancy uppercase text-white leading-[0.9]" style={{ fontSize: "clamp(36px, 5vw, 72px)", textShadow: "3px 3px 0 rgba(0,0,0,0.3)" }}>
                Contests. <span className="text-striped">Prizes.</span>
              </h2>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/20 backdrop-blur border border-white/30">
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-white">● Members only</span>
            </div>
          </div>
        {contests.length === 0 ? (
          <div className="bg-black/25 backdrop-blur p-8 rounded-3xl text-center text-sm text-white border border-white/20">Watch this space.</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {contests.map((c) => {
              const entered = enteredIds.includes(c.id);
              return (
              <div key={c.id} className="rounded-3xl border-2 border-white/30 bg-black/30 backdrop-blur p-5 card-mag" data-testid={`contest-${c.id}`}>
                {c.image_url && <img src={c.image_url} alt="" className="w-full aspect-video object-cover rounded-2xl mb-3" />}
                <div className="font-heading font-bold text-white text-base">{c.title}</div>
                <div className="text-[12px] text-white/80 mt-1 line-clamp-3">{c.description}</div>
                {c.prize && <div className="text-[10px] font-mono uppercase tracking-widest text-lime mt-3">Prize: {c.prize}</div>}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className={`chip !text-[9px] ${c.status === "live" ? "!text-lime !border-lime/40" : "!text-white/60 !border-white/30"}`}>{c.status}</span>
                  {entered ? (
                    <span className="chip !text-[9px] !text-lime !border-lime/50" data-testid={`contest-entered-${c.id}`}>Entered ✓</span>
                  ) : (
                    <button
                      onClick={() => enterContest(c)}
                      disabled={c.status !== "live"}
                      className="btn-pill-white !py-1.5 !px-3 !text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`contest-enter-${c.id}`}
                    >
                      Enter →
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
        </div>
      </section>

      {/* Opportunities */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16" data-testid="clubvip-opportunities" data-reveal="1">
        <div className="mb-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-skyx">Opportunities</div>
          <h2 className="font-display text-3xl md:text-4xl text-white">After parties. Trips. Tables.</h2>
        </div>
        {ops.length === 0 ? (
          <div className="glass p-8 rounded-3xl text-center text-sm text-white/70">Curating now.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {ops.map((o) => (
              <div key={o.id} className="rounded-3xl border border-skyx/40 bg-gradient-to-br from-skyx/10 to-purple/5 overflow-hidden card-mag" data-testid={`opportunity-${o.id}`}>
                {o.image_url && <img src={o.image_url} alt="" className="w-full aspect-[16/9] object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-skyx mb-2">
                    <span>{o.kind?.replace("_", " ")}</span>
                    {o.city && <span className="text-white/60">· {o.city}</span>}
                    {o.date && <span className="text-white/60">· {o.date}</span>}
                  </div>
                  <div className="font-heading font-bold text-white text-lg">{o.title}</div>
                  <p className="text-[13px] text-white/75 mt-2 line-clamp-3">{o.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    {o.price_hint && <span className="text-[11px] font-mono text-lime">{o.price_hint}</span>}
                    {o.request_link && <a href={o.request_link} target="_blank" rel="noreferrer" className="btn-lime !py-1.5 !px-3 !text-[10px]">Request Access →</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}


function MyEventShortlist({ eventId, eventTitle, onClearEvent }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    // Union of paid tickets + free RSVPs so match-by-event works for both.
    api.get("/clubvip/my-events").then(({ data }) => setRows((data || []).slice(0, 4))).catch(() => {});
  }, []);
  return (
    <div className="mt-5 space-y-2" data-testid="match-my-events">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Your events</div>
      {rows.length === 0 && <div className="text-[11px] text-muted">Book any event or RSVP to unlock event-scoped matching.</div>}
      {rows.map((r) => (
        <Link key={r.event_id} to={`/clubvip?event=${r.event_id}`} className={`block rounded-xl p-2.5 border transition-colors ${eventId === r.event_id ? "border-magenta bg-magenta/10" : "border-white/10 hover:border-white/25 bg-white/[0.03]"}`} data-testid={`match-event-${r.event_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime flex items-center gap-1">
            {r.event_date ? new Date(r.event_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}
            {r.source === "rsvp" && <span className="text-magenta">· RSVP</span>}
          </div>
          <div className="font-heading font-bold text-xs text-white truncate">{r.event_title || "Event"}</div>
          {r.event_city && <div className="text-[10px] text-muted truncate">{r.event_city}</div>}
        </Link>
      ))}
      {eventId && <button onClick={onClearEvent} className="text-[10px] font-mono uppercase tracking-widest text-magenta underline">Show all cities</button>}
    </div>
  );
}

function MatchesRail({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/clubvip/matches").then(({ data }) => setRows(data.matches || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0F0F16] p-5" data-testid="match-rail-your-matches">
      <div className="flex items-center justify-between">
        <div className="font-heading font-bold text-white">Your Matches</div>
        <button className="text-[10px] font-mono uppercase tracking-widest text-magenta">{rows.length} total</button>
      </div>
      {loading ? (
        <div className="text-[11px] text-muted mt-3">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[11px] text-muted mt-3">Start liking to see your matches here.</div>
      ) : (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {rows.slice(0, 8).map((m) => (
            <button key={m.match_id} onClick={() => onOpen(m)} className="group text-center" data-testid={`match-rail-${m.match_id}`}>
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                {m.blurred_photo ? <img src={m.blurred_photo} alt={m.first_name} className="w-full h-full object-cover" style={{ filter: "blur(6px) saturate(1.1)" }} /> : <div className="w-full h-full grid place-items-center"><Heart className="w-4 h-4 text-magenta" /></div>}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/85 mt-1 truncate">{m.first_name}</div>
            </button>
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div className="mt-3 rounded-2xl border border-magenta/40 bg-magenta/10 p-3 flex items-center gap-2" data-testid="match-rail-its-a-match">
          <div className="w-8 h-8 rounded-full bg-magenta grid place-items-center"><Heart className="w-4 h-4 text-white" fill="currentColor" /></div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-white text-xs leading-tight">It's a Match!</div>
            <div className="text-[10px] text-white/70 truncate">Tap a face above to start chatting</div>
          </div>
        </div>
      )}
    </div>
  );
}
